import type { SortingState } from '@tanstack/react-table';

export { TableDataState } from './table-types';
export type {
  GridColumnDef,
  GridColumnMeta,
  GridAccessorKey,
  SortingRule,
  TablePaginatedV8Ref,
  TablePaginatedV8Props,
  TablePaginatedV8CoreProps,
  TableEditableV8Props,
  TableRowClickContext,
  TableAppStore,
  TablePaginatedV8Component,
  TablePaginatedV8CoreComponent,
  TableEditableV8Component,
} from './table-types';

export { TablePaginatedV8Core } from './table-paginated-v8-core';
export { TableEditableV8, TablePaginatedV8 } from './table-editable-v8';

export { useColumnPersistence, resolveColumnUpdater } from './hooks/use-column-persistence';
export type { ColumnPersistenceSnapshot } from './hooks/use-column-persistence';
export { useTableEditing } from './hooks/use-table-editing';
export type { UseTableEditingOptions } from './hooks/use-table-editing';
export { usePaginatedTableV8 } from './hooks/use-paginated-table-v8';
export type { PaginatedTableV8State } from './hooks/use-paginated-table-v8';

export type StringKey<D> = Extract<keyof D, string>;
export type IdType<D> = StringKey<D> | string;

const EMPTY_SORTING: SortingState = [];

type Order2SortingColumn = {
  id?: string;
  orderByField?: string;
  accessorKey?: string;
};

/** Converts an API `orderBy` string (`"name ASC"`) to TanStack sorting state. */
export function order2Sorting(
  order: string,
  columns?: ReadonlyArray<Order2SortingColumn>,
): SortingState {
  const trimmed = order?.trim();
  if (!trimmed) return EMPTY_SORTING;

  const spaceIdx = trimmed.lastIndexOf(' ');
  const field = spaceIdx > 0 ? trimmed.slice(0, spaceIdx) : trimmed;
  const descOrAsc = spaceIdx > 0 ? trimmed.slice(spaceIdx + 1).toUpperCase() : 'ASC';

  let columnId = field;
  if (columns?.length) {
    const col = columns.find(
      (c) =>
        c.orderByField === field ||
        c.id === field ||
        (typeof c.accessorKey === 'string' && c.accessorKey === field),
    );
    if (col?.id) {
      columnId = col.id;
    }
  }

  return [{ id: columnId, desc: descOrAsc === 'DESC' }];
}

export { EMPTY_SORTING };
